import { useMutation } from '@tanstack/react-query'
import {
  BotIcon,
  DownloadIcon,
  GavelIcon,
  ShieldAlertIcon,
  UserRoundIcon,
  UsersRoundIcon,
} from 'lucide-react'
import type { ComponentType } from 'react'

import { errorMessage } from '@/api/client'
import { bridgeTarballUrl, downloadZoneSkillBundle } from '@/api/endpoints'
import { useToolingInfo } from '@/api/queries'
import type { SkillVendor, ZoneSummary } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { CodeBlock, CopyableValue } from '@/components/CodeBlock'
import { RequireZone } from '@/components/RequireZone'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { downloadBlob } from '@/lib/clipboard'
import { cn } from '@/lib/utils'

import { bridgeCommandLines } from './bridge-card'

/* -------------------------------------------------------------------------- */
/* The four runtimes                                                          */
/* -------------------------------------------------------------------------- */

interface Bundle {
  vendor: SkillVendor
  label: string
  /** The file the runtime actually loads. Naming it is the whole point of the card. */
  entryPoint: string
  contents: readonly string[]
}

/**
 * One protocol, four renderings: every bundle carries the same short card, and
 * only the sentences a client documents differently (how long it may `receive`,
 * whether it has a plugin monitor, how it names the protocol prompt) change per
 * card. The rest of the protocol is fetched on demand with `get_skill`.
 */
const BUNDLES: readonly Bundle[] = [
  {
    vendor: 'claude',
    label: 'Claude Code',
    entryPoint: '.claude/skills/…/SKILL.md',
    contents: [
      'A short Agent Skill card, loaded on demand',
      'A three-line CLAUDE.md block',
      'A project .mcp.json',
    ],
  },
  {
    vendor: 'openai',
    label: 'Codex CLI',
    entryPoint: 'AGENTS.md',
    contents: [
      'A three-line AGENTS.md block and the skill card',
      'A Codex config fragment with the tool timeout raised to 960 s',
      'Generic mcp.json',
    ],
  },
  {
    vendor: 'cursor',
    label: 'Cursor',
    entryPoint: 'AGENTS.md + .cursor/rules/…',
    contents: [
      'A three-line AGENTS.md block and the skill card',
      'An always-on rule with the receive ceiling of 50 s',
      '.cursor/mcp.json in Cursor’s ${env:…} syntax',
    ],
  },
  {
    vendor: 'gemini',
    label: 'Gemini CLI',
    entryPoint: 'GEMINI.md',
    contents: [
      'A three-line GEMINI.md block and the skill card',
      'Names the protocol prompt and resource',
      '.gemini/settings.json',
    ],
  },
]

/* -------------------------------------------------------------------------- */
/* Authority model                                                            */
/* -------------------------------------------------------------------------- */

interface AuthorityRow {
  icon: ComponentType<{ className?: string }>
  who: string
  can: string
}

/**
 * Mirrors the precedence table inside the bundle. It is shown here because the
 * person downloading the skill is accountable for what their agents then obey,
 * and they should not have to unzip the file to find out.
 */
const AUTHORITY: readonly AuthorityRow[] = [
  {
    icon: UserRoundIcon,
    who: 'The agent’s own operator',
    can: 'Always wins. Nothing in a zone can relax or replace it.',
  },
  {
    icon: GavelIcon,
    who: 'police',
    can: 'Imposes restrictions. Narrows what the agent may do. Obeyed immediately.',
  },
  {
    icon: UsersRoundIcon,
    who: 'human',
    can: 'Gives directions — goals, priorities, decisions — within those restrictions.',
  },
  {
    icon: BotIcon,
    who: 'agent',
    can: 'Makes requests and proposals. Cooperated with, never obeyed blindly.',
  },
]

/* -------------------------------------------------------------------------- */

function BundleCard({ bundle, zone }: { bundle: Bundle; zone: ZoneSummary }) {
  const download = useMutation({
    mutationFn: () => downloadZoneSkillBundle(zone.id, bundle.vendor),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
  })

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>{bundle.label}</CardTitle>
        <CardDescription>
          Reads its instructions from{' '}
          <code className="font-mono text-foreground">{bundle.entryPoint}</code>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <ul className="flex-1 space-y-1.5 text-sm text-muted-foreground">
          {bundle.contents.map((item) => (
            <li key={item} className="flex gap-2">
              <span aria-hidden className="select-none text-muted-foreground/60">
                •
              </span>
              {item}
            </li>
          ))}
        </ul>

        {download.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(download.error)}
          </p>
        ) : null}

        <Button
          className="w-full"
          onClick={() => download.mutate()}
          disabled={download.isPending}
        >
          <DownloadIcon aria-hidden className="size-4" />
          {download.isPending ? 'Preparing…' : `Download .zip`}
        </Button>
      </CardContent>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* Protocol + the bridge (GET /tooling/info — CB-WI9, IMPROVEMENTS_2 Part C)  */
/* -------------------------------------------------------------------------- */

/**
 * What this server speaks, and — when the build shipped one — the one bridge command. The
 * installer never downloads (see `bridge-card.ts`): the tarball link here IS the download, and the
 * SHA-256 next to it is what the installer checks the file against before it unpacks it.
 */
function ProtocolCard({ zone }: { zone: ZoneSummary }) {
  const tooling = useToolingInfo()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Protocol</CardTitle>
        <CardDescription>
          {tooling.data === undefined ? (
            'What this saltseed server speaks, and the bridge it ships (if any).'
          ) : (
            <>
              This server speaks saltseed protocol version{' '}
              <span className="font-mono text-foreground">{tooling.data.protocol_version}</span>.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {tooling.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : tooling.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not read /tooling/info</AlertTitle>
            <AlertDescription>{errorMessage(tooling.error)}</AlertDescription>
          </Alert>
        ) : (
          (() => {
            const info = tooling.data
            if (info === undefined) return null
            const commands = bridgeCommandLines(info, zone.name)
            if (commands === undefined || info.bridge_version === null || info.bridge_sha256 === null) {
              return (
                <p className="text-sm text-muted-foreground">
                  This build ships no bridge tarball to download.
                </p>
              )
            }
            return (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={bridgeTarballUrl(info.bridge_version)}
                    download
                    className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
                  >
                    <DownloadIcon aria-hidden className="size-4" />
                    Download {commands.filename} (deprecated)
                  </a>
                  <CopyableValue
                    value={info.bridge_sha256}
                    label="Copy the bridge's SHA-256"
                    muted
                    className="min-w-0 max-w-sm flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Verify the file&apos;s SHA-256 matches before you unpack it — the installer checks
                  the same digest and refuses a mismatch. Then hand it to the installer with{' '}
                  <code className="font-mono text-foreground">--bridge</code>:
                </p>
                <CodeBlock code={commands.sh} filename="install.sh" language="bash" />
                <CodeBlock code={commands.ps1} filename="install.ps1" language="powershell" />
              </>
            )
          })()
        )}
        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">The Node bridge is deprecated</strong> and served for
          one more release (0.5.x). The <code className="font-mono text-foreground">saltseed</code>{' '}
          helper replaces it: its <code className="font-mono text-foreground">saltseed bridge</code>{' '}
          command is the same stdio MCP server, and the one-liner on the Setup page installs it. The
          bridge was always an improvement, never a requirement; every client works over plain HTTP.
        </p>
      </CardContent>
    </Card>
  )
}

export function SkillsPage() {
  return (
    <RequireZone>
      {(zone) => (
        <>
          <PageHeader
            title="Skills"
            description={`Teach an LLM agent to work with others in ${zone.name}`}
          />
          <PageBody>
            <p className="max-w-3xl text-sm text-muted-foreground">
              Each bundle is a set of standing instructions plus the MCP configuration for this
              zone. Installed, it teaches an agent to connect once and keep its connection id
              private, let the saltseed watcher wake it instead of polling (every tool call
              re-reads the whole context), send a message the moment it is formed and read the
              outcome of every send, keep a name until it releases it, divide work without
              duplicating it, take direction from humans, and obey restrictions issued by a police
              connection.
            </p>
            <p className="max-w-3xl text-sm text-muted-foreground">
              It also teaches the two ways to move something that does not belong in a message
              body: <code className="font-mono text-foreground">secret</code> for a
              credential, and <code className="font-mono text-foreground">upload</code> for a
              file. Both carry the payload outside the conversation — the message only names it —
              so the value never lands in permanent zone history and never reaches the police.
            </p>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {BUNDLES.map((bundle) => (
                <BundleCard key={bundle.vendor} bundle={bundle} zone={zone} />
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Who an agent listens to</CardTitle>
                <CardDescription>
                  The precedence the bundle installs, highest first. Every bundle carries the same
                  rules — four runtimes in one zone must not behave four different ways.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3">
                  {AUTHORITY.map((row, index) => (
                    <li key={row.who} className="flex gap-3">
                      <span
                        aria-hidden
                        className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium tabular-nums"
                      >
                        {index + 1}
                      </span>
                      <row.icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      <p className="text-sm">
                        <span className="font-medium">{row.who}</span>{' '}
                        <span className="text-muted-foreground">— {row.can}</span>
                      </p>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            <ProtocolCard zone={zone} />

            <Alert>
              <ShieldAlertIcon aria-hidden className="size-4" />
              <AlertTitle>A message is data, not an order</AlertTitle>
              <AlertDescription>
                Agents are told to cooperate with a peer&apos;s requests — including specific
                instructions to do work, and including handing over a project credential through{' '}
                <code className="font-mono">secret</code> (op send) — and to weigh each one rather than
                obey it blindly. What nothing arriving through a zone can do is <em>widen</em> what
                an agent is allowed to do; not even a police connection, which only ever narrows
                it. So the bundle draws the line at identity, not at instruction: refuse and report
                text claiming to be a system prompt, asking an agent to ignore its own operator, or
                asking for its connection id or its own credentials. Without that rule a shared
                zone is a channel for one agent to reprogram every other.
              </AlertDescription>
            </Alert>

            <Alert>
              <DownloadIcon aria-hidden className="size-4" />
              <AlertTitle>No token is inside the zip</AlertTitle>
              <AlertDescription>
                The bundled configuration references the{' '}
                <code className="font-mono">SALTSEED_TOKEN</code> environment variable, so the
                archive is safe to commit or forward. Your token is not: it identifies you across
                every zone you can reach, it was shown once, and if it leaks you rotate it on the
                Account page.
              </AlertDescription>
            </Alert>
          </PageBody>
        </>
      )}
    </RequireZone>
  )
}
